import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import ContainerLayout from "../../../../components/container-layout/ContainerLayout";
import NavigateListCustom from "../../../../components/navigation/NavigationListCustom";
import { routeName } from "../../../../routes/routeName";
import { NavigateFunction, useNavigate, useParams } from "react-router-dom";
import ContainerButton from "../../../../components/button/ContainerButton";
import {
  UploadedFile,
  InputValueProjectForm,
  defualtValueProjectForm,
  ProjectSubmissionData,
} from "../components/domain";
import {
  setLoading,
  unsetLoading,
} from "../../../../store/loading/loading.slice";
import {
  alertActive,
  alertError,
  alertSuccess,
} from "../../../../components/alert/swal";
import { edpexProjectAPI } from "../../../../api/edpex-project/edpex-project.api";
import { EDPEX_PROJECT_STATUS } from "../enum";
import { edpexFormMasterAPI } from "../../../../api/edpex-form-master/edpex-form-master.api";
import AccordionUpload from "../../../../components/accordion/AccordionUpload";
import { colors } from "../../../../constants/colors";
import IndicatorFormResult from "../components/IndicatorFormResult";
import { RootState } from "../../../../store/store";

const FormCompleteResponsibleProject = () => {
  const baseURL = useSelector((state:RootState) => state.api.image_api)
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate: NavigateFunction = useNavigate();
  const { id } = useParams();

  const isEditMode = Boolean(id);

  const [inputValue, setInputValue] = useState<InputValueProjectForm>(
    defualtValueProjectForm
  );

  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [uploadedFilesProject, setUploadedFilesProject] = useState<
    UploadedFile[]
  >([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [projectStatusCheck, setProjectStatusCheck] = useState<string>("");

  const handleGetDataFile = async () => {
    try {
      dispatch(setLoading());

      const res = await edpexFormMasterAPI.getEdpexFormMasterByType({
        form_type: "EXAMPLE_SUMMARY",
        is_release: 1,
      });

      if (res.data?.status < 300 && res.data?.data) {
        const { document_files = [] } = res.data.data;

        const mapFileData = (
          files: any[],
          type: "FILE_IMAGE" | "FILE_DOCUMENT"
        ): UploadedFile[] =>
          files.map((file) => ({
            id: file.id_edpex_form_file,
            name: file.file_name,
            size: file.file_size
              ? `${(file.file_size / 1024).toFixed(2)} KB`
              : "Unknown",
            uploadDate: file.modified_date || file.created_date || "N/A",
            file_path: file.file_path,
            fullUrl: `${baseURL}${file.file_path}${encodeURIComponent(
              file.file_name
            )}`,
            type,
            file: null as unknown as File, // Placeholder for UI compatibility
          }));

        setUploadedFiles([...mapFileData(document_files, "FILE_DOCUMENT")]);
      }
    } catch (error: any) {
      console.error("Get data error:", error);
      alertError(t("เกิดข้อผิดพลาด"), t("โหลดข้อมูลไม่สำเร็จ"));
    } finally {
      dispatch(unsetLoading());
    }
  };

  useEffect(() => {
    handleGetDataFile();
  }, []);

  useEffect(() => {
    if (isEditMode && id) {
      loadProjectData(parseInt(id));
    }
  }, [id, isEditMode]);

  function convertFromApiFormat(apiData: any) {
    const formData: any = {
      edpex_project_indicators: [],
      edpex_project_budgets: [],
      edpex_project_responsible: [],
    };

    // Convert indicators back
    if (apiData.edpex_project_indicators) {
      formData.edpex_project_indicators = apiData.edpex_project_indicators.map(
        (indicator: any) => ({
          id: indicator.id_edpex_project_indicators
            ? indicator.id_edpex_project_indicators.toString()
            : "",
          name: indicator.edpex_project_indicators || "",
          type: indicator.edpex_project_indicators_target || "LEVEL",
          value: indicator.edpex_project_indicators_target_point || "",
          value_2: indicator.edpex_project_indicators_point || "",
          value_3: indicator.edpex_project_indicators_point_result || "",
        })
      );
    }

    // Convert budgets back
    if (apiData.edpex_project_budgets) {
      formData.edpex_project_budgets = apiData.edpex_project_budgets.map(
        (budget: any) => ({
          spending_info_id: budget.id_edpex_project_budget
            ? budget.id_edpex_project_budget.toString()
            : "",
          spending_info_month: budget.fiscal_month || "",
          spending_info_year: budget.fiscal_year || "",
          spending_info_value: budget.edpex_project_budget_amount
            ? budget.edpex_project_budget_amount.toString()
            : "0",
        })
      );
    }

    // Convert responsible persons back
    if (apiData.edpex_project_responsible) {
      formData.edpex_project_responsible =
        apiData.edpex_project_responsible.map((person: any) => ({
          id_employee: person.id_edpex_responsible || 0,
          name: person.name,
          employee_no: person.employee_no,
          status: person.edpex_project_responsible_type || "PRIMARY",
        }));
    }

    return formData;
  }

  const loadProjectData = useCallback(
    async (projectId: number) => {
      try {
        setIsLoading(true);
        dispatch(setLoading());

        const response = await edpexProjectAPI.getEdpexProjectById(projectId);

        if (response.data.success && response.data.data) {
          const projectData = response.data.data;

          const formData = convertFromApiFormat(projectData);

          setProjectStatusCheck(projectData.edpex_project_status);
          // Map API data to form structure
          setInputValue({
            project_name: projectData.edpex_project_name || "",
            fiscal_year: +projectData.fiscal_year + 543,
            start_date: projectData.edpex_project_start_date || "",
            end_date: projectData.edpex_project_end_date || "",
            budget: projectData.edpex_project_budget || 0,
            project_type: projectData.edpex_project_status || "",
            edpex_project_ongoing: projectData.edpex_project_ongoing,
            edpex_project_strategic_work:
              projectData.edpex_project_strategic_work,
            id_project_continue: projectData.id_edpex_project_ref || 0,

            responsible_department:
              projectData.edpex_project_department_responsible || "",
            responsible_director:
              projectData.edpex_project_user_responsible || "",
            responsible_work:
              projectData.edpex_project_division_responsible || "",

            edpex_mission: projectData.id_edpex_mission?.toString() || "",
            edpex_criteria: projectData.id_edpex_standard_point || 0,
            edpex_criteria_sub: projectData.id_edpex_standard_point_detail || 0,
            objective: projectData.edpex_project_objective || "",
            target_group: projectData.edpex_project_target_group || "",
            edpex_result: projectData.edpex_project_result || "",
            // indicatorList: projectData.edpex_project_indicators || [],
            // spendingInfo: projectData.edpex_project_budgets || [],
            // responsiblePersons: projectData.edpex_project_responsible || [],
            indicatorList: formData.edpex_project_indicators,
            spendingInfo: formData.edpex_project_budgets,
            responsiblePersons: formData.edpex_project_responsible,
            //
            description: projectData.edpex_project_remark || "",
            project_status: projectData.edpex_project_status || "",
          });

          if (
            projectData.edpex_project_files &&
            Array.isArray(projectData.edpex_project_files)
          ) {
            setUploadedFilesProject(
              projectData.edpex_project_files.map((file: any) => ({
                id: file.id,
                name: file.original_name || file.name,
                size: file.file_size || 0,
                type: file.mime_type || file.type || "",
                file_path: file.file_path || file.url,
              }))
            );
          }
        } else {
          throw new Error(response.message || t("error.loadProjectFailed"));
        }
      } catch (error: any) {
        console.error("Error loading project data:", error);
        alertError(error.message || t("error.loadProjectFailed"));
      } finally {
        setIsLoading(false);
        dispatch(unsetLoading());
      }
    },
    [dispatch, t]
  );

  const handleFileUpload = async (files: File[]): Promise<number[]> => {
    if (files.length === 0) return [];

    try {
      const formData = new FormData();
      files.forEach((file: File) => {
        formData.append("files", file);
      });

      const res = await edpexProjectAPI.uploadEdpexProjectFile(formData);

      // Extract file IDs from the correct path: res.data.data
      const fileIds =
        res.data?.data?.map((file: any) => file.id_edpex_project_file) || [];
      return fileIds;
    } catch (error: any) {
      console.error("File upload error:", error);
      alertError(
        t("เกิดข้อผิดพลาด"),
        error?.response?.data?.message || t("อัปโหลดไฟล์ไม่สำเร็จ")
      );
      return [];
    }
  };

  function convertToApiFormat(formData: any) {
    const apiData = {
      edpex_project_indicators: [],
      edpex_project_budgets: [],
      edpex_project_responsible: [],
    };

    // Convert indicators
    if (formData.edpex_project_indicators) {
      apiData.edpex_project_indicators = formData.edpex_project_indicators.map(
        (indicator: any, index: number) => ({
          id_edpex_project_indicators: +indicator.id || null,
          seq: +index,
          edpex_project_indicators: indicator.name || "",
          edpex_project_indicators_target: indicator.type || "",
          edpex_project_indicators_target_point: indicator.value || "",
          edpex_project_indicators_point: indicator.value_2 || "",
          edpex_project_indicators_point_result: indicator.value_3 || "",
        })
      );
    }

    // Convert budgets
    if (formData.edpex_project_budgets) {
      apiData.edpex_project_budgets = formData.edpex_project_budgets.map(
        (budget: any) => ({
          id_edpex_project_budget: budget.spending_info_id || null,
          fiscal_year: budget.spending_info_year || "",
          fiscal_month: budget.spending_info_month || "",
          edpex_project_budget_amount:
            parseFloat(budget.spending_info_value) || 0,
        })
      );
    }

    // Convert responsible persons
    if (formData.edpex_project_responsible) {
      apiData.edpex_project_responsible =
        formData.edpex_project_responsible.map((person: any) => ({
          id_edpex_project_responsible: person.id || null,
          edpex_project_responsible_type: person.status || "PRIMARY",
          id_edpex_responsible: person.id_employee || 0,
        }));
    }

    return apiData;
  }

  const buildProjectSubmissionData =
    useCallback(async (): Promise<any> => {
      try {
        const newFileIds = await handleFileUpload(
          uploadedFilesProject.map((file) => file.file).filter(Boolean)
        );

        const allFileIds = [
          ...uploadedFilesProject.map((file) => file.id).filter((id) => id),
          ...newFileIds,
        ];

        const formBodyData = {
          edpex_project_indicators: inputValue.indicatorList,
          edpex_project_budgets: inputValue.spendingInfo,
          edpex_project_responsible: inputValue.responsiblePersons,
        };

        const apiFormatData = convertToApiFormat(formBodyData);

        return apiFormatData.edpex_project_indicators;

      } catch (error) {
        console.error("Error building submission data:", error);
        throw error; // rethrow to let calling code handle it
      }
    }, [inputValue, uploadedFiles, isEditMode]);

  // Update project
  const handleUpdateProject = useCallback(
    async (data: ProjectSubmissionData) => {
      if (!id) throw new Error("Project ID is required for update");

      const response = await edpexProjectAPI.updateEdpeProjectComplete(
        parseInt(id),
        data
      );

      if (response.data.success) {
        alertSuccess(
          "สรุปผลยืนยันเสร็จสิ้นโครงการ/กิจกรรม/งาน",
          "สรุปผลยืนยันเสร็จสิ้นโครงการ/กิจกรรม/งาน เรียบร้อยแล้ว",
          undefined,
          () => {
            loadProjectData(parseInt(id));
          }
        );
      } else {
        alertError("สรุปผลยืนยันเสร็จสิ้นโครงการ/กิจกรรม/งาน ไม่สำเร็จ");
      }
    },
    [id, navigate, t]
  );

  const handleSubmit = useCallback(async () => {
    try {
      dispatch(setLoading());

      const submissionData = await buildProjectSubmissionData();

      await handleUpdateProject(submissionData);
    } catch (error: any) {
      alertError("เกิดข้อผิดพลาด");
    } finally {
      dispatch(unsetLoading());
    }
  }, [dispatch, buildProjectSubmissionData, handleUpdateProject, t]);

  return (
    <>
      <ContainerLayout
        title={t("สรุปผลยืนยันเสร็จสิ้นโครงการ/กิจกรรม/งาน")}
        hidden_sub_title
        sub_title_custom={
          <>
            {/* <Box
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
              onClick={() => {
                setShowModal(true);
              }}
            >
              <InfoIcon color="disabled" />
              <Typography>ข้อมูล</Typography>
            </Box> */}
          </>
        }
        navigateContent={
          <NavigateListCustom
            navigateData={[
              {
                key: "1",
                name: t("ระบบ Edpex"),
                disabled_match: true,
                pathName: routeName.cms.edpexResultDisplay,
              },
              {
                key: "2",
                name: t("สรุปผลยืนยันเสร็จสิ้นโครงการ/กิจกรรม/งาน"),
                pathName: routeName.cms.edpexResponsibleProjectsComplete,
              },
            ]}
          />
        }
        buttonContent={
          <ContainerButton
            justifyContent="flex-end"
            buttonData={
              id && projectStatusCheck === EDPEX_PROJECT_STATUS.PROCESS
                ? [
                    {
                      buttonProps: {
                        onClick: () => {
                          navigate(-1);
                        },
                      },
                      key: "CANCEL",
                      label: t("ยกเลิก"),
                      seq: 1,
                      type: "CANCEL",
                    },
                    {
                      buttonProps: {
                        onClick: () => {
                          // (true);
                          alertActive(
                            "สรุปผลยืนยันเสร็จสิ้นโครงการ/กิจกรรม/งาน",
                            "ท่านต้องการสรุปผลยืนยันเสร็จสิ้นโครงการ/กิจกรรม/งาน ใช่หรือไม่",
                            undefined,
                            undefined,
                            async () => {
                              handleSubmit();
                            }
                          );
                        },
                      },
                      key: "SAVE",
                      label: t("ยืนยัน"),
                      seq: 3,
                      type: "SAVE",
                    },
                  ]
                : [
                    {
                      buttonProps: {
                        onClick: () => {
                          navigate(-1);
                        },
                      },
                      key: "CANCEL",
                      label: t("ยกเลิก"),
                      seq: 1,
                      type: "CANCEL",
                    },
                  ]
            }
          />
        }
        content={
          <>
            <AccordionUpload
              defaultExpanded
              required
              enableButtonLeft
              titleName="ข้อมูลตัวอย่างการสรุปผล"
              uploadedFiles={uploadedFiles}
              setUploadedFiles={setUploadedFiles}
              acceptedFileTypes=".pdf,.doc,.docx,.jpg,.png,.xlsx"
              maxFileSize={5} // 5MB limit
              multiple={true}
              isView={true}
              colorHeader={colors.subPrimary}
            />
            <br />

            <IndicatorFormResult
              inputValue={inputValue}
              setInputValue={setInputValue}
              disabled={projectStatusCheck !== EDPEX_PROJECT_STATUS.PROCESS}
            />

            <br />
            <AccordionUpload
              defaultExpanded
              required
              enableButtonLeft
              titleName="ไฟล์เอกสาร"
              uploadedFiles={uploadedFilesProject}
              setUploadedFiles={setUploadedFilesProject}
              acceptedFileTypes=".pdf,.doc,.docx,.jpg,.png,.xlsx"
              maxFileSize={5} // 5MB limit
              multiple={true}
              isView={projectStatusCheck !== EDPEX_PROJECT_STATUS.PROCESS}
              // onFileUpload={handleFileUpload} // Pass the upload handler if needed by component
              // onRemoveFile={handleRemoveFile} // Pass the remove handler if needed
            />
          </>
        }
      />
    </>
  );
};

export default FormCompleteResponsibleProject;
